import { useCallback, useEffect, useRef, useState, type RefObject } from "react";

import { GasCloudOverlay, type GasCloudProps } from "./GasCloudOverlay.js";
import { PlantDiagram } from "./PlantDiagram.js";
import type { Cluster } from "./ClusterCard.js";
import type { ClusterState } from "../api/telemetry.js";
import { beamGeometry, type SmoothPtz, type Waypoint, panCoverageDeg, RING_THRESHOLD_DEG } from "../ptz-geometry.js";

/**
 * La vista IMPIANTO, portata da `FocusMode.qml`.
 *
 * Sotto c'e' la planimetria caricata da Settings, o il diagramma stilizzato se
 * non ne e' stata caricata nessuna. Sopra ci vanno il fascio del laser, che
 * segue la testa, e la nuvola di gas quando la concentrazione la giustifica.
 *
 * Il dettaglio che fa la differenza fra un disegno giusto e uno che sbaglia di
 * mezzo riquadro: le coordinate del sensore e del bersaglio sono normalizzate
 * 0-1 **sull'immagine disegnata**, non sul contenitore. Un'immagine 4:3 dentro
 * un riquadro 16:9 lascia due bande nere ai lati, e ancorare l'overlay al
 * contenitore sposterebbe il fascio di tutta quella banda. Per questo qui si
 * calcola il rettangolo davvero dipinto e l'overlay ci sta dentro.
 */
export function PlantView({
  cluster,
  telemetry,
  livePpm,
  ptz,
  waypoints,
}: {
  cluster: Cluster;
  telemetry?: ClusterState | undefined;
  livePpm?: number | undefined;
  ptz: RefObject<SmoothPtz>;
  waypoints: Waypoint[];
}) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [rect, setRect] = useState({ left: 0, top: 0, width: 0, height: 0 });

  const hasImage = Boolean(cluster.plantImageUrl);

  // Il rettangolo effettivamente dipinto: con l'immagine e' il risultato del
  // "contain", col diagramma e' tutto il riquadro.
  const measure = useCallback(() => {
    const box = boxRef.current;
    if (!box) return;
    const bw = box.clientWidth;
    const bh = box.clientHeight;
    if (bw <= 0 || bh <= 0) return;
    const img = imgRef.current;
    if (!hasImage || !img || img.naturalWidth === 0 || img.naturalHeight === 0) {
      setRect({ left: 0, top: 0, width: bw, height: bh });
      return;
    }
    const ar = img.naturalWidth / img.naturalHeight;
    const w = ar > bw / bh ? bw : bh * ar;
    const h = ar > bw / bh ? bw / ar : bh;
    setRect({ left: (bw - w) / 2, top: (bh - h) / 2, width: w, height: h });
  }, [hasImage]);

  useEffect(() => {
    measure();
    const box = boxRef.current;
    if (!box) return;
    const ro = new ResizeObserver(measure);
    ro.observe(box);
    return () => ro.disconnect();
  }, [measure]);

  // Giro completo o ventaglio: si deduce dai waypoint della traccia attiva.
  const is360 = panCoverageDeg(waypoints) >= RING_THRESHOLD_DEG;

  const sensorX = Number(cluster.sensorX) || 0.5;
  const sensorY = Number(cluster.sensorY) || 0.95;
  const laserEnd0X = Number(cluster.laserEndX) || 0.5;
  const laserEnd0Y = Number(cluster.laserEndY) || 0.05;

  const windSpeed = telemetry?.windSpeed;
  const windDirection = telemetry?.windDirection;

  // Chiamata a ogni fotogramma dalle tele dell'overlay: la posizione della
  // testa e' interpolata fuori dallo stato di React, apposta.
  const sample = useCallback((): GasCloudProps => {
    const p = ptz.current ?? { angle: 0, tilt: 0, zoom: 30, tiltFrac: 0 };
    const g = beamGeometry({
      is360,
      panDeg: p.angle,
      tiltFrac: p.tiltFrac,
      sensorX,
      sensorY,
      laserEnd0X,
      laserEnd0Y,
      boxW: rect.width,
      boxH: rect.height,
    });
    return {
      ppm: livePpm ?? 0,
      windSpeed: windSpeed ?? 0,
      windDirection: windDirection ?? 0,
      sensorX: g.pivotX,
      sensorY: g.pivotY,
      laserEndX: g.endX,
      laserEndY: g.endY,
    };
  }, [
    ptz,
    is360,
    sensorX,
    sensorY,
    laserEnd0X,
    laserEnd0Y,
    rect.width,
    rect.height,
    livePpm,
    windSpeed,
    windDirection,
  ]);

  return (
    <div className="plantview">
      <div className="plantview__box" ref={boxRef}>
        {hasImage ? (
          <img
            className="plantview__img"
            ref={imgRef}
            src={cluster.plantImageUrl ?? ""}
            alt=""
            onLoad={measure}
          />
        ) : (
          <PlantDiagram />
        )}
        <div
          className="plantview__content"
          style={{
            left: `${rect.left}px`,
            top: `${rect.top}px`,
            width: `${rect.width}px`,
            height: `${rect.height}px`,
          }}
        >
          <GasCloudOverlay sample={sample} />
        </div>
      </div>

      {/* L'indicatore del vento compare solo se l'impianto ha l'anemometro e
          il dato sta arrivando: una rosa dei venti ferma su zero direbbe
          "aria calma" dove invece non si sta misurando niente. */}
      {Boolean(cluster.windEnabled) && windSpeed !== undefined && (
        <div className="windbox">
          <WindArrow direction={windDirection ?? 0} />
          <b>{windSpeed.toFixed(1)} m/s</b>
          {windDirection !== undefined && <span>{Math.round(windDirection)}°</span>}
        </div>
      )}
    </div>
  );
}

function WindArrow({ direction }: { direction: number }) {
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" aria-hidden="true">
      <g transform={`translate(20 20) rotate(${direction})`} stroke="#60a5fa" fill="#60a5fa">
        <path d="M0 12 L0 -12" strokeWidth="2.5" />
        <path d="M-5 -6 L0 -16 L5 -6 Z" stroke="none" />
      </g>
    </svg>
  );
}
